// Notices every way the signed-in account can change and hands the account
// that went to resetAccount. Signing out lets the reader's data go at once.
// A different account signing in lets the previous one's go, whether in this
// page, after a sign-in redirect, or in another tab of the same browser (the
// stored grant is shared, so this tab follows it). A session that merely ran
// out keeps the reader's data for their return; a refresh that fails on the
// network changes nothing.

import type { User } from 'oidc-client-ts';

import { resetAccount, resumeAccount } from '@/lib/auth/accountReset';
import type { SingleFlightUserManager } from '@/lib/auth/userManager';

/** Whose data this browser holds: the last account signed in, until it signs out. */
export const ACCOUNT_RECORD_KEY = 'knowsh.account';

function recorded(): string | undefined {
  try {
    return window.localStorage.getItem(ACCOUNT_RECORD_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function record(subject: string | undefined): void {
  try {
    if (subject) window.localStorage.setItem(ACCOUNT_RECORD_KEY, subject);
    else window.localStorage.removeItem(ACCOUNT_RECORD_KEY);
  } catch {
    // Without storage there is nothing stored to let go of later either.
  }
}

export class AccountWatch {
  /** The account signed in in this page. */
  private current: string | undefined;

  constructor(private readonly manager: SingleFlightUserManager) {}

  /** An account is signed in here: a different one before it, in this page or on record, is let go. */
  adopt(subject: string | undefined): void {
    if (!subject) return;
    const previous = this.current ?? recorded();
    if (previous && previous !== subject) resetAccount(previous);
    this.current = subject;
    resumeAccount(subject);
    record(subject);
  }

  /**
   * Starts following the manager and the other tabs. Called only once the
   * page's restore attempt has settled: that attempt drops a dead grant with
   * removeUser, which is a session running out, not the reader signing out.
   */
  watch(): () => void {
    const events = this.manager.events;
    const onLoaded = (user: User) => this.adopt(user.profile.sub);
    const onUnloaded = () => this.signedOut();
    const onStorage = (event: StorageEvent) => {
      if (event.key === ACCOUNT_RECORD_KEY) this.recordDroppedElsewhere(event.oldValue, event.newValue);
      else if (event.key === null || event.key.endsWith(this.manager.storedUserKey)) void this.followOtherTab();
    };
    // A page restored from the back-forward cache heard no storage events while it was away.
    const onPageShow = (event: { persisted: boolean }) => {
      if (event.persisted) void this.followOtherTab();
    };
    events.addUserLoaded(onLoaded);
    events.addUserUnloaded(onUnloaded);
    window.addEventListener('storage', onStorage);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      events.removeUserLoaded(onLoaded);
      events.removeUserUnloaded(onUnloaded);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pageshow', onPageShow);
    };
  }

  private signedOut(): void {
    const ended = this.current;
    if (!ended) return;
    this.current = undefined;
    record(undefined);
    resetAccount(ended);
  }

  /**
   * Only a sign-out removes the record. Another tab's removal of the stored
   * user can reach this tab before the record's removal does, and then looks
   * like a session running out; this lets that account's data go after all.
   */
  private recordDroppedElsewhere(was: string | null, now: string | null): void {
    if (was && !now) resetAccount(was);
  }

  /**
   * Another tab signed in, out, or as someone else. This tab lets its account
   * go from memory and tells react-oidc-context, which never reads the store
   * again on its own. The stored data goes too, unless the account merely
   * lost its session there: no one else signed in and the record still names it.
   */
  private async followOtherTab(): Promise<void> {
    const user = await this.manager.getUser();
    const subject = user?.profile.sub;
    if (subject === this.current) return;
    const ended = this.current;
    this.current = subject;
    if (ended) resetAccount(ended, { keepStored: !subject && recorded() === ended });
    if (subject) resumeAccount(subject);
    if (user) await this.manager.events.load(user);
    else await this.manager.events.unload();
  }
}
